import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { CATEGORIES, PLATFORMS, CATEGORY_PLATFORMS, DEFAULT_SUBCATEGORIES, PLATFORM_MODELS } from '@siliconvault/shared';
import { useVault } from '../context/VaultContext';

export function useTaxonomySuggestions(
  field: 'category' | 'platform' | 'subcategory',
  filterCategory?: string,
  filterPlatform?: string
) {
  const { activeVaultId, vaultTypeDef } = useVault();

  // Use vault type definition for fallback defaults, or fall back to retro computer constants
  const vtCategories = vaultTypeDef?.categories ?? CATEGORIES;
  const vtBrands = vaultTypeDef?.brands ?? PLATFORMS;
  const vtCategoryBrands = vaultTypeDef?.categoryBrands ?? CATEGORY_PLATFORMS;
  const vtSubcategories = vaultTypeDef?.subcategories ?? DEFAULT_SUBCATEGORIES;
  const vtBrandModels = vaultTypeDef?.brandModels ?? PLATFORM_MODELS;

  // Build fallback defaults while loading
  let fallback: string[] = [];
  if (field === 'category') {
    fallback = [...vtCategories];
  } else if (field === 'platform') {
    fallback = filterCategory && vtCategoryBrands[filterCategory]
      ? [...vtCategoryBrands[filterCategory]]
      : [...vtBrands];
  } else if (field === 'subcategory') {
    const catDefaults = filterCategory ? (vtSubcategories[filterCategory] ?? []) : [];
    const platModels = filterPlatform ? (vtBrandModels[filterPlatform] ?? []) : [];
    const seen = new Set<string>();
    fallback = [];
    for (const v of [...catDefaults, ...platModels]) {
      const lower = v.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        fallback.push(v);
      }
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['taxonomy', field, filterCategory, filterPlatform, activeVaultId],
    queryFn: () => apiClient.getTaxonomySuggestions(field, filterCategory, filterPlatform, activeVaultId ?? undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });

  return {
    suggestions: data?.suggestions ?? fallback,
    isLoading,
  };
}
